import type {ComposerAttachment} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {
  ImplicitContextPageContext,
  ImplicitContextPageResolverContext,
} from '@github-ui/copilot-shared-ui/contexts/ImplicitContextPageContext'
import {
  implicitAttachmentIcon,
  implicitAttachmentKey,
  implicitAttachmentUrl,
} from '@github-ui/copilot-shared-ui/references/implicit-context'
import {useImplicitDismissals} from '@github-ui/copilot-shared-ui/contexts/ImplicitDismissalsContext'
import {useImplicitContextAttachments} from '@github-ui/copilot-shared-ui/references/use-implicit-references'
import type {MessageAttachment} from '@microsoft/agent-host-protocol'
import {use, useCallback, useMemo} from 'react'

const IMPLICIT_ATTACHMENT_PREFIX = 'implicit-attachment:'

/** Chips for implicit attachments, addressable by {@link isImplicitChipId}. */
export function implicitAttachmentChips(attachments: readonly MessageAttachment[]): ComposerAttachment[] {
  return attachments.map(attachment => ({
    id: `${IMPLICIT_ATTACHMENT_PREFIX}${implicitAttachmentKey(attachment)}`,
    label: attachment.label ?? '',
    icon: implicitAttachmentIcon(attachment),
    url: implicitAttachmentUrl(attachment),
  }))
}

export function isImplicitChipId(id: string): boolean {
  return id.startsWith(IMPLICIT_ATTACHMENT_PREFIX)
}

export interface ImplicitDraftAttachments {
  /** What the page resolved to, minus what the user dismissed. */
  attachments: MessageAttachment[]
  /** Drops a chip the user removed, so no later turn carries it. */
  dismissChip: (chipId: string) => void
  /**
   * The attachments to send with a turn, awaiting the in-flight page resolution
   * so a fast submit can't drop the page context. `excludedKeys` are the ones
   * the turn already carries explicitly.
   */
  resolve: (excludedKeys: ReadonlySet<string>) => Promise<MessageAttachment[]>
  /** Drops what an accepted turn carried, so the next turn doesn't repeat it. */
  settle: (submitted: readonly MessageAttachment[]) => void
}

/**
 * The host page's implicit context for a session that does not exist yet.
 *
 * The established composer resolves the same context, so without this the first
 * turn would be the only one in the conversation that did not know which page
 * it came from — which on a pull request is the turn that needs it most.
 * Resolution is shared with the established composer through one query, so the
 * swap from composing to conversing does not refetch it.
 *
 * Disabled drafts resolve nothing: a repository-backed background task picks
 * its own repository rather than being seeded with the page the user happens to
 * be reading.
 */
export function useImplicitDraftAttachments({enabled}: {enabled: boolean}): ImplicitDraftAttachments {
  const page = use(ImplicitContextPageContext)
  const getLatestPage = use(ImplicitContextPageResolverContext)
  const {attachments: resolved, resolveAttachments} = useImplicitContextAttachments(
    enabled ? page : null,
    enabled && getLatestPage ? getLatestPage : undefined,
  )

  // Shared with the established composer rather than held here: this surface is
  // swapped out the moment the first turn is accepted, so a dismissal kept
  // locally would be forgotten by the composer that sends every turn after it.
  const {dismissed, peek: peekDismissed, dismiss: dismissKeys} = useImplicitDismissals()

  const attachments = useMemo(
    () => (enabled ? resolved.filter(attachment => !dismissed.has(implicitAttachmentKey(attachment))) : []),
    [dismissed, enabled, resolved],
  )

  const dismissChip = useCallback(
    (chipId: string) => dismissKeys([chipId.slice(IMPLICIT_ATTACHMENT_PREFIX.length)]),
    [dismissKeys],
  )

  const resolve = useCallback(
    async (excludedKeys: ReadonlySet<string>) => {
      if (!enabled) return []
      const attachmentsForTurn = await resolveAttachments()
      return attachmentsForTurn.filter(attachment => {
        const key = implicitAttachmentKey(attachment)
        return !peekDismissed().has(key) && !excludedKeys.has(key)
      })
    },
    [enabled, peekDismissed, resolveAttachments],
  )

  const settle = useCallback(
    (submitted: readonly MessageAttachment[]) => dismissKeys(submitted.map(implicitAttachmentKey)),
    [dismissKeys],
  )

  return {attachments, dismissChip, resolve, settle}
}
